import {
  CheckCircle2,
  CreditCard,
  Mail,
  Receipt,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input, LoadingSpinner, Modal } from '../components/ui';
import { usePage } from '../contexts';
import { useCurrency } from '../hooks';
import { billingService, contactService } from '../services';
import {
  BillingTransaction,
  SubscriptionResponse,
  TokenPackage,
} from '../types/entities';

const Subscription: React.FC = () => {
  const { t } = useTranslation();
  const { setPageInfo } = usePage();
  const { currency } = useCurrency();
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [plans, setPlans] = useState<TokenPackage[]>([]);
  const [transactions, setTransactions] = useState<BillingTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [processingPlanId, setProcessingPlanId] = useState<number | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCustomPlanModalOpen, setIsCustomPlanModalOpen] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<{
    name: string;
    email: string;
    phone: string;
    message: string;
  }>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      message: '',
    },
  });

  const loadData = useCallback(async (refresh: boolean = false) => {
    if (refresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    try {
      const [plansResponse, subscriptionResponse, transactionsResponse] =
        await Promise.all([
          billingService.getPackages(),
          billingService.getSubscription().catch(() => null),
          billingService.getTransactions({ page: 1, page_size: 20 }),
        ]);

      setPlans(
        [...plansResponse].sort((a, b) => {
          const aPrice = a.price_usd || 0;
          const bPrice = b.price_usd || 0;
          return aPrice - bPrice;
        })
      );
      setSubscription(subscriptionResponse);
      setTransactions(transactionsResponse.items || []);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('subscription.loadError');
      toast.error(message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    setPageInfo(t('subscription.title'), t('subscription.subtitle'));
  }, [setPageInfo, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const currentPackageId = useMemo(
    () => (subscription?.package_id ? Number(subscription.package_id) : null),
    [subscription]
  );
  const chargedTransactions = useMemo(() => {
    const chargedStatuses = [
      'paid',
      'succeeded',
      'success',
      'completed',
      'captured',
      'settled',
    ];
    const blockedStatuses = [
      'pending',
      'failed',
      'canceled',
      'cancelled',
      'unpaid',
      'open',
      'expired',
      'refunded',
      'refund',
    ];

    return transactions.filter(transaction => {
      const status = (transaction.status || '').toLowerCase();
      const amount = transaction.amount ?? 0;

      if (amount <= 0) {
        return false;
      }

      if (!status) {
        return false;
      }

      if (blockedStatuses.some(blocked => status.includes(blocked))) {
        return false;
      }

      return chargedStatuses.some(charged => status.includes(charged));
    });
  }, [transactions]);

  const getPlanAmountAndCurrency = (plan: TokenPackage): {
    amount: number;
    code: string;
  } => {
    const selectedCurrency = currency.toUpperCase();

    if (selectedCurrency === 'BRL' && typeof plan.price_brl === 'number') {
      return { amount: plan.price_brl, code: 'BRL' };
    }

    if (selectedCurrency === 'EUR' && typeof plan.price_eur === 'number') {
      return { amount: plan.price_eur, code: 'EUR' };
    }

    return { amount: plan.price_usd, code: 'USD' };
  };

  const formatPlanPrice = (plan: TokenPackage): string => {
    const { amount, code } = getPlanAmountAndCurrency(plan);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatTransactionAmount = (transaction: BillingTransaction): string => {
    const amount = transaction.amount ?? 0;
    const code = (transaction.currency || 'USD').toUpperCase();

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const intervalLabel = (plan: TokenPackage): string => {
    if (plan.interval === 'year') {
      return t('subscription.intervalYearly');
    }
    return t('subscription.intervalMonthly');
  };

  const handleCheckout = async (plan: TokenPackage) => {
    setProcessingPlanId(plan.id);
    try {
      const response = await billingService.purchasePackage(
        plan.id,
        currency.toLowerCase()
      );
      toast.success(
        t('subscription.redirectingToCheckout', { name: response.package_name })
      );
      setTimeout(() => {
        window.location.href = response.checkout_url;
      }, 200);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('subscription.errorCheckout');
      toast.error(message);
      setProcessingPlanId(null);
    }
  };

  const handleChangePlan = async (planId: number) => {
    setProcessingPlanId(planId);
    try {
      await billingService.changePlan({ target_package_id: planId });
      toast.success(t('subscription.planChangedSuccess'));
      await loadData(true);
      setProcessingPlanId(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('subscription.changePlanError');
      toast.error(message);
      setProcessingPlanId(null);
    }
  };

  const handleCancelPlan = async () => {
    setIsCancelling(true);
    try {
      await billingService.cancelSubscription({ cancel_at_period_end: true });
      toast.success(t('subscription.cancelSuccess'));
      await loadData(true);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('subscription.cancelError');
      toast.error(message);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSubmitCustomPlan = async (values: {
    name: string;
    email: string;
    phone: string;
    message: string;
  }) => {
    try {
      await contactService.submitContact(values);
      toast.success(t('tokens.contactSuccess'));
      setIsCustomPlanModalOpen(false);
      reset();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('tokens.contactError');
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('subscription.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('subscription.subtitle')}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => loadData(true)}
          loading={isRefreshing}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('common.update')}
        </Button>
      </div>

      <Card>
        <div className="p-3">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('subscription.currentPlan')}
            </h3>
          </div>

          {!subscription?.has_subscription ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('subscription.noActivePlan')}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('subscription.currentPlan')}
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {subscription.package_name || '-'}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('subscription.status')}
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {subscription.status || '-'}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('subscription.nextRenewal')}
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {subscription.current_period_end
                    ? new Date(subscription.current_period_end).toLocaleDateString(
                        'pt-BR'
                      )
                    : '-'}
                </p>
              </div>
            </div>
          )}

          {subscription?.has_subscription && (
            <div className="mt-4">
              <Button
                variant="error"
                size="sm"
                onClick={handleCancelPlan}
                loading={isCancelling}
              >
                <XCircle className="h-4 w-4 mr-2" />
                {t('subscription.cancelPlan')}
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div className="p-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('subscription.availablePlans')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {plans.map(plan => {
              const isCurrent = currentPackageId === plan.id;
              const isProcessing = processingPlanId === plan.id;

              return (
                <div
                  key={plan.id}
                  className={`rounded-xl border p-3 transition-colors ${
                    isCurrent
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                        {plan.name}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {plan.description || '-'}
                      </p>
                    </div>
                    {isCurrent && (
                      <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                    )}
                  </div>

                  <div className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                      {formatPlanPrice(plan)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {intervalLabel(plan)}
                    </p>
                  </div>

                  <div className="mt-3">
                    {isCurrent ? (
                      <Button className="w-full" variant="success" size="sm" disabled>
                        {t('subscription.currentPlan')}
                      </Button>
                    ) : subscription?.has_subscription ? (
                      <Button
                        className="w-full"
                        variant="outline"
                        size="sm"
                        onClick={() => handleChangePlan(plan.id)}
                        loading={isProcessing}
                      >
                        {t('subscription.changePlan')}
                      </Button>
                    ) : (
                      <Button
                        className="w-full"
                        size="sm"
                        onClick={() => handleCheckout(plan)}
                        loading={isProcessing}
                      >
                        {t('subscription.subscribe')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="rounded-xl border border-dashed border-primary-300 dark:border-primary-700 p-3 bg-primary-50/40 dark:bg-primary-900/10">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                    {t('subscription.customPlanTitle')}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('subscription.customPlanDescription')}
                  </p>
                </div>
                <Mail className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>

              <div className="mt-3 rounded-lg bg-white dark:bg-gray-800 p-3 border border-primary-100 dark:border-primary-800">
                <p className="text-sm font-semibold text-primary-700 dark:text-primary-300">
                  {t('subscription.customPlanCtaHint')}
                </p>
              </div>

              <div className="mt-3">
                <Button
                  className="w-full"
                  size="sm"
                  onClick={() => setIsCustomPlanModalOpen(true)}
                >
                  {t('tokens.contactEnterprise')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-3">
          <div className="flex items-center gap-2 mb-4">
            <Receipt className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('subscription.transactions')}
            </h3>
          </div>

          {chargedTransactions.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('subscription.noTransactions')}
            </p>
          ) : (
            <div className="space-y-2">
              {chargedTransactions.map(transaction => (
                <div
                  key={transaction.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {transaction.package_name || t('subscription.unknownPlan')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {transaction.created_at
                        ? new Date(transaction.created_at).toLocaleString('pt-BR')
                        : '-'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatTransactionAmount(transaction)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {transaction.status || '-'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Modal
        isOpen={isCustomPlanModalOpen}
        onClose={() => setIsCustomPlanModalOpen(false)}
        title={t('tokens.contactTitle')}
        size="md"
      >
        <form onSubmit={handleSubmit(handleSubmitCustomPlan)} className="space-y-4">
          <Input
            label={t('tokens.contactName')}
            placeholder={t('tokens.contactNamePlaceholder')}
            error={errors.name?.message}
            {...register('name', { required: t('tokens.contactNameRequired') })}
            required
          />
          <Input
            label={t('tokens.contactEmail')}
            placeholder={t('tokens.contactEmailPlaceholder')}
            error={errors.email?.message}
            type="email"
            {...register('email', {
              required: t('tokens.contactEmailRequired'),
              pattern: {
                value: /^\S+@\S+\.\S+$/,
                message: t('tokens.contactEmailInvalid'),
              },
            })}
            required
          />
          <Input
            label={t('tokens.contactPhone')}
            placeholder={t('tokens.contactPhonePlaceholder')}
            error={errors.phone?.message}
            {...register('phone', { required: t('tokens.contactPhoneRequired') })}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('tokens.contactMessage')}
            </label>
            <textarea
              rows={4}
              className="w-full px-3 py-2 border border-light-300 dark:border-graphite-600 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-50 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder={t('tokens.contactMessagePlaceholder')}
              {...register('message', {
                required: t('tokens.contactMessageRequired'),
              })}
            />
            {errors.message?.message && (
              <p className="mt-1 text-sm text-error-600 dark:text-error-400">
                {errors.message.message}
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsCustomPlanModalOpen(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={isSubmitting}
            >
              {t('tokens.contactSubmit')}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Subscription;
